// /home/ec2-user/brand-insights-dashboard/frontend/src/components/Scorecards.js
import React, { useState, useEffect, useMemo, useRef } from 'react';

import {
  Card, CardContent, Typography, Box, Grid,
  LinearProgress, Chip, Alert,
  CircularProgress, Button, Stack, IconButton
} from '@mui/material';

import {
  Dashboard, Article, ThumbUp, Favorite,
  Comment, Share, Insights,
  ShowChart,  Visibility,
  PushPin,          // Icona per pinned
  PushPinOutlined   // Icona per unpinned
} from '@mui/icons-material';


// Backend API base URL (deve essere lo stesso di App.js)
const API_BASE = 'https://brand-insights-backend.nexallai.com';

// ============================================
// 0. FUNZIONI HELPER (PER OTTIMIZZAZIONE)
// ============================================

const getZeroMetrics = () => ({
  total_posts: 0,
  total_interactions: 0,
  total_likes: 0,
  total_comments: 0,
  total_shares: 0,
  total_views: 0,
  avg_likes_per_post: 0,
  avg_comments_per_post: 0,
  avg_shares_per_post: 0,
  avg_media_count_per_post: 0
});

const calculateMetrics = (dashboardData) => {
  if (!dashboardData) return getZeroMetrics();
  
  const totalPosts = dashboardData.total_posts || 0;
  const totalViews = dashboardData.total_views || 0;
  
  return {
    total_posts: totalPosts,
    total_interactions: dashboardData.total_interactions || 0,
    total_likes: dashboardData.total_likes || 0,
    total_comments: dashboardData.total_comments || 0,
    total_shares: dashboardData.total_shares || 0,
    total_views: totalViews,
    avg_likes_per_post: dashboardData.avg_likes_per_post || 0,
    avg_comments_per_post: dashboardData.avg_comments_per_post || 0,
    avg_shares_per_post: dashboardData.avg_shares_per_post || 0,
    avg_media_count_per_post: dashboardData.avg_media_count_per_post || 0
  };
};

// ============================================
// 1. COMPONENTI DI BASE
// ============================================

export const KPICard = ({
  title,
  value,
  icon: Icon,
  color = 'primary',
  subtitle,
  format = 'number',
  trend = null,
  loading = false,
  size = 'small' // Impostato small di default per le card più piccole
}) => {
  const formattedValue = useMemo(() => {
    if (loading) return '...';
    if (value === undefined || value === null) return 'N/A';

    switch(format) {
      case 'number':
        const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
        if (numValue >= 1000000) {
          return `${(numValue / 1000000).toFixed(1)}M`;
        } else if (numValue >= 1000) {
          return `${(numValue / 1000).toFixed(1)}K`;
        }
        return numValue.toLocaleString();
      case 'percent':
        const percentValue = typeof value === 'number' ? value : parseFloat(value) || 0;
        return `${percentValue.toFixed(1)}%`;
      case 'decimal':
        const decimalValue = typeof value === 'number' ? value : parseFloat(value) || 0;
        return decimalValue.toFixed(2);
      case 'currency':
        const currencyValue = typeof value === 'number' ? value : parseFloat(value) || 0;
        return `$${currencyValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      default:
        return value;
    }
  }, [value, format, loading]);

  const isSmall = size === 'small';
  const isLarge = size === 'large';

  return (
    <Card sx={{
      bgcolor: `${color}.50`,
      height: '100%',
      minHeight: 90, // Altezza ridotta
      opacity: loading ? 0.7 : 1,
      border: '1px solid',
      borderColor: `${color}.200`,
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 2,
        borderColor: `${color}.main`
      }
    }}>
      <CardContent sx={{
        p: 1.5, // Padding ridotto
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header with icon and title */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
          minHeight: 24 // Altezza ridotta
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden'
          }}>
            <Icon sx={{
              mr: 0.75, // Margine ridotto
              color: `${color}.main`,
              fontSize: '1rem' // Dimensione ridotta
            }} />
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{
                fontSize: '0.75rem', // Font più piccolo
                fontWeight: 500
              }}
            >
              {title}
            </Typography>
          </Box>
          
          {/* Trend indicator if provided */}
          {trend && (
            <Chip
              label={trend > 0 ? `+${trend}%` : `${trend}%`}
              size="small"
              color={trend > 0 ? 'success' : 'error'}
              variant="outlined"
              sx={{ height: 18, fontSize: '0.6rem' }} // Dimensioni ridotte
            />
          )}
        </Box>
        
        {/* Main value - CENTRATO VERTICALMENTE */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
          minHeight: 40 // Altezza ridotta
        }}>
          <Typography
            variant={isLarge ? "h3" : isSmall ? "h4" : "h3"}
            sx={{
              fontWeight: 'bold',
              color: `${color}.main`,
              lineHeight: 1,
              fontSize: '1.5rem', // Dimensione ridotta
              textAlign: 'center'
            }}
          >
            {formattedValue}
          </Typography>
        </Box>
        
        {/* Loading indicator */}
        {loading && (
          <LinearProgress
            color={color}
            sx={{
              mt: 0.5,
              height: 2,
              borderRadius: 1
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

// ============================================
// 2. MAIN SCORECARDS COMPONENT - 6 KPI CARDS
// ============================================

const SocialPerformanceMetrics = ({ dashboardData, loading = false, discoveryFilters = null }) => {
  // Stato per il toggle sticky - VERSIONE SEMPLICE
  const [isPinned, setIsPinned] = useState(false);
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState('100%');
  
  // useMemo OTTIMIZZATO - calcola solo quando dashboardData cambia
  const metrics = useMemo(() => {
    return calculateMetrics(dashboardData);
  }, [dashboardData]);

  // Misura dimensioni della card
  useEffect(() => {
    if (cardRef.current) {
      const height = cardRef.current.offsetHeight;
      const width = cardRef.current.offsetWidth;
      
      setCardHeight(height);
      // Salva la larghezza originale per quando diventa fixed
      if (!isPinned && width > 0) {
        setContainerWidth(`${width}px`);
      }
    }
  }, [isPinned, metrics, loading]);
  
  // Return condizionale OTTIMIZZATO
  if (!dashboardData && !loading) {
    return null;
  }
  
  // 6 KPI cards totali (rimosse: Impressions, Reach, Engagement Rate, Avg Views)
  const allKPIs = [
    // Prime 6 metriche principali
    {
      key: 'total_posts',
      title: "Total Posts",
      value: metrics.total_posts,
      icon: Article,
      color: 'primary',
      format: 'number',
      size: 'small',
      subtitle: ""
    },
    {
      key: 'total_interactions',
      title: "Total Interactions", 
      value: metrics.total_interactions,
      icon: ThumbUp,
      color: 'success',
      format: 'number',
      size: 'small',
      subtitle: ""
    },
    {
      key: 'total_likes',
      title: "Total Likes",
      value: metrics.total_likes,
      icon: Favorite,
      color: 'error',
      format: 'number',
      size: 'small',
      subtitle: ""
    },
    {
      key: 'total_comments',
      title: "Total Comments",
      value: metrics.total_comments,
      icon: Comment,
      color: 'warning',
      format: 'number',
      size: 'small',
      subtitle: ""
    },
    {
      key: 'total_shares',
      title: "Total Shares",
      value: metrics.total_shares,
      icon: Share,
      color: 'secondary',
      format: 'number',
      size: 'small',
      subtitle: ""
    },
    {
      key: 'total_views',
      title: "Total Views",
      value: metrics.total_views,
      icon: Visibility,
      color: 'info',
      format: 'number',
      size: 'small',
      subtitle: ""
    }
  ];

  // Debug info
  const hasRealData = metrics.total_posts > 0 || metrics.total_interactions > 0;

  return (
    <>
      {/* Spazio fantasma quando pinned */}
      {isPinned && (
        <Box 
          sx={{ 
            height: cardHeight,
            mb: 3,
            visibility: 'hidden',
            pointerEvents: 'none'
          }}
        />
      )}
      
      {/* Scorecard principale - VERSIONE MIGLIORATA */}
      <Box
        ref={cardRef}
        sx={{
          // POSIZIONE FIXED quando pinned - ma con layout IDENTICO
          position: isPinned ? 'fixed' : 'relative',
          top: isPinned ? '135px' : 'auto',
          left: isPinned ? '50%' : 'auto',
          transform: isPinned ? 'translateX(-50%)' : 'none',
          zIndex: isPinned ? 1100 : 1,
          width: isPinned ? containerWidth : '100%', // Mantiene la stessa larghezza
          mb: isPinned ? 0 : 3,
          
          // NESSUN cambiamento di layout interno quando pinned
          // I contenuti rimangono ESATTAMENTE dove sono
          
          // Stili base (IDENTICI in entrambi gli stati)
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: isPinned ? 'primary.main' : 'divider',
          boxShadow: isPinned 
            ? '0 4px 20px rgba(25, 118, 210, 0.2), 0 1px 3px rgba(0,0,0,0.1)' 
            : '0 1px 3px rgba(0,0,0,0.1)',
          
          // Transizione SOLO per effetti visivi (border, shadow)
          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
          
          // Effetto hover solo quando non pinned
          ...(!isPinned && {
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              borderColor: 'primary.light'
            }
          }),
          
          // Media query per dispositivi mobili
          '@media (max-width: 600px)': {
            position: 'relative',
            top: 'auto',
            left: 'auto',
            transform: 'none',
            width: '100%',
            mb: 3
          }
        }}
      >
        <Card sx={{
          borderRadius: 2,
          boxShadow: 'none',
          backgroundColor: 'transparent',
          border: 'none',
          // CONTENUTO ASSOLUTAMENTE INVARIATO
        }}>
          <CardContent sx={{ 
            p: 2,
            '&:last-child': {
              pb: 2
            }
          }}>
            {/* Header con toggle sticky - CONTENUTO INVARIATO */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5
            }}>
              <Typography variant="h6" sx={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                color: isPinned ? 'primary.main' : 'text.primary'
              }}>
                <Dashboard sx={{
                  mr: 1,
                  color: isPinned ? 'primary.main' : 'primary.main',
                  fontSize: '1.2rem'
                }} /> 
                Social Performance Dashboard
              </Typography>
              
              {/* Toggle pin + Data status - CONTENUTO INVARIATO */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5
              }}>
                {/* Toggle Pin Button */}
                <IconButton 
                  size="small"
                  onClick={() => setIsPinned(!isPinned)}
                  color={isPinned ? "primary" : "default"}
                  sx={{
                    width: 32,
                    height: 32,
                    border: '1px solid',
                    borderColor: isPinned ? 'primary.main' : 'divider',
                    backgroundColor: isPinned ? 'primary.50' : 'transparent',
                    '&:hover': {
                      backgroundColor: isPinned ? 'primary.100' : 'action.hover',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                  title={isPinned ? "Unpin (scroll with page)" : "Pin to top"}
                >
                  {isPinned ? (
                    <PushPin sx={{ 
                      fontSize: '1.1rem',
                      color: 'primary.main'
                    }} />
                  ) : (
                    <PushPinOutlined sx={{ 
                      fontSize: '1.1rem',
                      color: 'text.secondary'
                    }} />
                  )}
                </IconButton>
                
                {/* Data status indicator */}
                {loading ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    minWidth: 80 
                  }}>
                    <CircularProgress size={14} sx={{ mr: 0.5 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Loading...
                    </Typography>
                  </Box>
                ) : hasRealData ? (
                  <Chip 
                    label="Loaded" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    sx={{ 
                      height: 24, 
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}
                  />
                ) : (
                  <Chip 
                    label="No Data" 
                    size="small" 
                    color="default" 
                    variant="outlined"
                    sx={{ 
                      height: 24, 
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* TUTTE le KPI Cards - LAYOUT ASSOLUTAMENTE INVARIATO */}
            <Grid container spacing={1} sx={{ mb: 0 }}>
              {allKPIs.map((metric) => (
                <Grid item xs={6} sm={4} md={2} key={metric.key}>
                  <KPICard
                    title={metric.title}
                    value={metric.value}
                    icon={metric.icon}
                    color={metric.color}
                    format={metric.format}
                    size={metric.size}
                    subtitle={metric.subtitle}
                    loading={loading}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Empty state - INVARIATO */}
            {!loading && !hasRealData && (
              <Alert 
                severity="info" 
                sx={{ 
                  mt: 2, 
                  p: 1,
                  border: '1px solid',
                  borderColor: 'info.light',
                  fontSize: '0.85rem'
                }}
                icon={<Insights fontSize="small" />}
              >
                No social media data found for the selected filters.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

// ============================================
// 3. PLATFORM DISTRIBUTION - VERSIONE AGGIORNATA
// ============================================

const PlatformDistribution = ({ filters, loading = false }) => {
  const [platformData, setPlatformData] = useState([]);
  const [platformLoading, setPlatformLoading] = useState(false);
  const [platformError, setPlatformError] = useState(null);

  // Effetto per caricare i dati delle piattaforme
  useEffect(() => {
    const fetchPlatformData = async () => {
      // Non fare nulla se non c'è progetto selezionato
      if (!filters.project_id) {
        setPlatformData([]);
        return;
      }

      try {
        setPlatformLoading(true);
        setPlatformError(null);

        // Costruisci i parametri per l'API
        const params = new URLSearchParams();
        params.append('dimension', 'platform');

        if (filters.project_id) {
          params.append('project_id', filters.project_id);
        }

        if (filters.client_id) {
          params.append('client_id', filters.client_id);
        }

        if (filters.posted_at_range?.start) {
          params.append('start_date', filters.posted_at_range.start.toISOString().split('T')[0]);
        }

        if (filters.posted_at_range?.end) {
          params.append('end_date', filters.posted_at_range.end.toISOString().split('T')[0]);
        }

        if (filters.brand && filters.brand.length > 0) {
          params.append('brand', filters.brand.join(','));
        }

        if (filters.post_type && filters.post_type.length > 0) {
          params.append('post_type', filters.post_type.join(','));
        }

        if (filters.source_post_id && filters.source_post_id.length > 0) {
          params.append('source_post_id', filters.source_post_id.join(','));
        }

        const url = `${API_BASE}/api/v1/analytics/breakdown?${params}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        setPlatformData(data.items || []);
        
      } catch (err) {
        console.error('❌ Errore caricamento dati platform:', err);
        setPlatformError(`Errore: ${err.message}`);
        setPlatformData([]);
      } finally {
        setPlatformLoading(false);
      }
    };

    // Debounce per evitare troppe chiamate
    const timeoutId = setTimeout(fetchPlatformData, 300);
    return () => clearTimeout(timeoutId);
  }, [
    filters.project_id,
    filters.client_id,
    filters.posted_at_range,
    filters.brand,
    filters.post_type
  ]);

  // Funzione per formattare i numeri
  const formatNum = (num) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`.replace('.0', '');
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`.replace('.0', '');
    }
    return num.toLocaleString();
  };

  // Calcola totali per le percentuali
  const totalPosts = platformData.reduce((sum, p) => sum + (p.total_posts || 0), 0);
  const totalInteractions = platformData.reduce((sum, p) => sum + (p.total_interactions || 0), 0);
  const totalLikes = platformData.reduce((sum, p) => sum + (p.total_likes || 0), 0);
  const totalComments = platformData.reduce((sum, p) => sum + (p.total_comments || 0), 0);
  const totalShares = platformData.reduce((sum, p) => sum + (p.total_shares || 0), 0);
  const totalViews = platformData.reduce((sum, p) => sum + (p.total_views || 0), 0);

  if (platformLoading || loading) {
    return (
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontSize: '1rem' }}>
            <ShowChart sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Platform Distribution
          </Typography>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={18} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (platformError) {
    return (
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontSize: '1rem' }}>
            <ShowChart sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Platform Distribution
          </Typography>
          <Alert severity="error" sx={{ mt: 1 }}>
            <Typography variant="body2">
              Errore nel caricamento dati piattaforme: {platformError}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!platformData.length) {
    return (
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontSize: '1rem' }}>
            <ShowChart sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Platform Distribution
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" py={1.5}>
            Nessun dato piattaforme disponibile
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const sortedPlatforms = [...platformData].sort((a, b) =>
    (b.total_interactions || 0) - (a.total_interactions || 0)
  );

  return (
    <Card sx={{ mb: 2.5 }}>
      <CardContent sx={{ p: 2 }}>
        {/* HEADER CON STATS */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
            <ShowChart sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Platform Distribution
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {sortedPlatforms.length} platform • {formatNum(totalPosts)} posts • {formatNum(totalInteractions)} interactions
          </Typography>
        </Box>

        {/* INTESTAZIONE TABELLA */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1.5fr repeat(6, 1fr)',
          gap: 1,
          mb: 1,
          px: 1,
          pb: 0.5,
          borderBottom: '2px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            PLATFORM
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            📝 POSTS
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            👍 INTERACTIONS
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            ❤️ LIKES
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            💬 COMMENTS
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            🔄 SHARES
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            👁️ VIEWS
          </Typography>
        </Box>

        {/* CONTAINER CON SCROLL */}
        <Box sx={{
          maxHeight: 350,
          overflowY: 'auto',
          pr: 0.5,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '2px' },
          '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '2px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#555' }
        }}>
          {/* RIGHE DELLA TABELLA */}
          <Stack spacing={0.75}>
            {sortedPlatforms.map((platform, idx) => {
              const postsPct = totalPosts ? Math.round((platform.total_posts / totalPosts) * 100) : 0;
              const interactionsPct = totalInteractions ? Math.round((platform.total_interactions / totalInteractions) * 100) : 0;
              const likesPct = totalLikes ? Math.round((platform.total_likes / totalLikes) * 100) : 0;
              const commentsPct = totalComments ? Math.round((platform.total_comments / totalComments) * 100) : 0;
              const sharesPct = totalShares ? Math.round((platform.total_shares / totalShares) * 100) : 0;
              const viewsPct = totalViews ? Math.round((platform.total_views / totalViews) * 100) : 0;

              // Colori per le piattaforme
              const platformColors = {
                instagram: '#E4405F',
                facebook: '#1877F2',
                tiktok: '#000000',
                linkedin: '#0A66C2',
                twitter: '#1DA1F2',
                youtube: '#FF0000',
                pinterest: '#E60023'
              };

              const platformColor = platformColors[platform.dimension_value?.toLowerCase()] || '#757575';

              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr repeat(6, 1fr)',
                    gap: 1,
                    alignItems: 'center',
                    p: 1,
                    borderRadius: 1,
                    bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  {/* COLONNA 1: NOME PLATFORM */}
                  <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: `${platformColor}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1,
                      flexShrink: 0
                    }}>
                      <Typography variant="caption" sx={{ color: platformColor, fontWeight: 'bold', fontSize: '0.7rem' }}>
                        {platform.dimension_value?.charAt(0).toUpperCase()}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        fontSize: '0.85rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textTransform: 'capitalize'
                      }}
                      title={platform.dimension_value}
                    >
                      {platform.dimension_value}
                    </Typography>
                  </Box>

                  {/* COLONNA 2: POSTS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(platform.total_posts)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={postsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'primary.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'primary.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {postsPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 3: INTERACTIONS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(platform.total_interactions)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={interactionsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'success.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'success.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {interactionsPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 4: LIKES */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(platform.total_likes)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={likesPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'error.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'error.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {likesPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 5: COMMENTS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(platform.total_comments)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={commentsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'warning.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'warning.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {commentsPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 6: SHARES */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(platform.total_shares)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={sharesPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'secondary.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'secondary.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {sharesPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 7: VIEWS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(platform.total_views)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={viewsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'info.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'info.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {viewsPct}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================
// 4. POST TYPE DISTRIBUTION
// ============================================

const PostTypeDistribution = ({ filters, loading = false }) => {
  const [postTypeData, setPostTypeData] = useState([]);
  const [postTypeLoading, setPostTypeLoading] = useState(false);
  const [postTypeError, setPostTypeError] = useState(null);

  // Effetto per caricare i dati dei post type
  useEffect(() => {
    const fetchPostTypeData = async () => {
      // Non fare nulla se non c'è progetto selezionato
      if (!filters.project_id) {
        setPostTypeData([]);
        return;
      }

      try {
        setPostTypeLoading(true);
        setPostTypeError(null);

        // Costruisci i parametri per l'API
        const params = new URLSearchParams();
        params.append('dimension', 'post_type');

        if (filters.project_id) {
          params.append('project_id', filters.project_id);
        }

        if (filters.client_id) {
          params.append('client_id', filters.client_id);
        }

        if (filters.posted_at_range?.start) {
          params.append('start_date', filters.posted_at_range.start.toISOString().split('T')[0]);
        }

        if (filters.posted_at_range?.end) {
          params.append('end_date', filters.posted_at_range.end.toISOString().split('T')[0]);
        }

        if (filters.brand && filters.brand.length > 0) {
          params.append('brand', filters.brand.join(','));
        }

        if (filters.platform && filters.platform.length > 0) {
          params.append('platform', filters.platform.join(','));
        }

        if (filters.source_post_id && filters.source_post_id.length > 0) {
          params.append('source_post_id', filters.source_post_id.join(','));
        }

        const url = `${API_BASE}/api/v1/analytics/breakdown?${params}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        setPostTypeData(data.items || []);
        
      } catch (err) {
        console.error('❌ Errore caricamento dati post type:', err);
        setPostTypeError(`Errore: ${err.message}`);
        setPostTypeData([]);
      } finally {
        setPostTypeLoading(false);
      }
    };

    // Debounce per evitare troppe chiamate
    const timeoutId = setTimeout(fetchPostTypeData, 300);
    return () => clearTimeout(timeoutId);
  }, [
    filters.project_id,
    filters.client_id,
    filters.posted_at_range,
    filters.brand,
    filters.platform
  ]);

  // Funzione per formattare i numeri (usa la stessa di PlatformDistribution)
  const formatNum = (num) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`.replace('.0', '');
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`.replace('.0', '');
    }
    return num.toLocaleString();
  };

  // Calcola totali per le percentuali
  const totalPosts = postTypeData.reduce((sum, p) => sum + (p.total_posts || 0), 0);
  const totalInteractions = postTypeData.reduce((sum, p) => sum + (p.total_interactions || 0), 0);
  const totalLikes = postTypeData.reduce((sum, p) => sum + (p.total_likes || 0), 0);
  const totalComments = postTypeData.reduce((sum, p) => sum + (p.total_comments || 0), 0);
  const totalShares = postTypeData.reduce((sum, p) => sum + (p.total_shares || 0), 0);
  const totalViews = postTypeData.reduce((sum, p) => sum + (p.total_views || 0), 0);

  if (postTypeLoading || loading) {
    return (
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontSize: '1rem' }}>
            <Article sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Post Type Distribution
          </Typography>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={18} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (postTypeError) {
    return (
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontSize: '1rem' }}>
            <Article sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Post Type Distribution
          </Typography>
          <Alert severity="error" sx={{ mt: 1 }}>
            <Typography variant="body2">
              Errore nel caricamento dati post type: {postTypeError}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!postTypeData.length) {
    return (
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontSize: '1rem' }}>
            <Article sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Post Type Distribution
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" py={1.5}>
            Nessun dato post type disponibile
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const sortedPostTypes = [...postTypeData].sort((a, b) =>
    (b.total_interactions || 0) - (a.total_interactions || 0)
  );

  return (
    <Card sx={{ mb: 2.5 }}>
      <CardContent sx={{ p: 2 }}>
        {/* HEADER CON STATS */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
            <Article sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Post Type Distribution
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {sortedPostTypes.length} post types • {formatNum(totalPosts)} posts • {formatNum(totalInteractions)} interactions
          </Typography>
        </Box>

        {/* INTESTAZIONE TABELLA - STESSA DI PLATFORM DISTRIBUTION */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1.5fr repeat(6, 1fr)',
          gap: 1,
          mb: 1,
          px: 1,
          pb: 0.5,
          borderBottom: '2px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            POST TYPE
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            📝 POSTS
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            👍 INTERACTIONS
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            ❤️ LIKES
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            💬 COMMENTS
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            🔄 SHARES
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            👁️ VIEWS
          </Typography>
        </Box>

        {/* CONTAINER CON SCROLL */}
        <Box sx={{
          maxHeight: 350,
          overflowY: 'auto',
          pr: 0.5,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '2px' },
          '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '2px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#555' }
        }}>
          {/* RIGHE DELLA TABELLA */}
          <Stack spacing={0.75}>
            {sortedPostTypes.map((postType, idx) => {
              const postsPct = totalPosts ? Math.round((postType.total_posts / totalPosts) * 100) : 0;
              const interactionsPct = totalInteractions ? Math.round((postType.total_interactions / totalInteractions) * 100) : 0;
              const likesPct = totalLikes ? Math.round((postType.total_likes / totalLikes) * 100) : 0;
              const commentsPct = totalComments ? Math.round((postType.total_comments / totalComments) * 100) : 0;
              const sharesPct = totalShares ? Math.round((postType.total_shares / totalShares) * 100) : 0;
              const viewsPct = totalViews ? Math.round((postType.total_views / totalViews) * 100) : 0;

              // Colori per i post type
              const postTypeColors = {
                reel: '#FF6B6B',
                image: '#4ECDC4',
                video: '#45B7D1',
                carousel: '#96CEB4',
                story: '#FFEAA7',
                live: '#DDA0DD',
                link: '#98D8C8'
              };

              const postTypeColor = postTypeColors[postType.dimension_value?.toLowerCase()] || '#757575';

              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr repeat(6, 1fr)',
                    gap: 1,
                    alignItems: 'center',
                    p: 1,
                    borderRadius: 1,
                    bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  {/* COLONNA 1: NOME POST TYPE */}
                  <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: `${postTypeColor}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1,
                      flexShrink: 0
                    }}>
                      <Typography variant="caption" sx={{ color: postTypeColor, fontWeight: 'bold', fontSize: '0.7rem' }}>
                        {postType.dimension_value?.charAt(0).toUpperCase()}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        fontSize: '0.85rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textTransform: 'capitalize'
                      }}
                      title={postType.dimension_value}
                    >
                      {postType.dimension_value}
                    </Typography>
                  </Box>

                  {/* COLONNA 2: POSTS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(postType.total_posts)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={postsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'primary.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'primary.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -8,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {postsPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 3: INTERACTIONS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(postType.total_interactions)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={interactionsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'success.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'success.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -8,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {interactionsPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 4: LIKES */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(postType.total_likes)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={likesPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'error.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'error.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -8,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {likesPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 5: COMMENTS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(postType.total_comments)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={commentsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'warning.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'warning.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -8,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {commentsPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 6: SHARES */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(postType.total_shares)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={sharesPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'secondary.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'secondary.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -8,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {sharesPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 7: VIEWS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(postType.total_views)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={viewsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'info.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'info.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -8,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {viewsPct}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================
// 5. BRAND DISTRIBUTION
// ============================================

const BrandDistribution = ({ filters, loading = false }) => {
  const [brandData, setBrandData] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandError, setBrandError] = useState(null);

  // Effetto per caricare i dati dei brand
  useEffect(() => {
    const fetchBrandData = async () => {
      // Non fare nulla se non c'è progetto selezionato
      if (!filters.project_id) {
        setBrandData([]);
        return;
      }

      try {
        setBrandLoading(true);
        setBrandError(null);

        // Costruisci i parametri per l'API
        const params = new URLSearchParams();
        params.append('dimension', 'brand');

        if (filters.project_id) {
          params.append('project_id', filters.project_id);
        }

        if (filters.client_id) {
          params.append('client_id', filters.client_id);
        }

        if (filters.posted_at_range?.start) {
          params.append('start_date', filters.posted_at_range.start.toISOString().split('T')[0]);
        }

        if (filters.posted_at_range?.end) {
          params.append('end_date', filters.posted_at_range.end.toISOString().split('T')[0]);
        }

        if (filters.platform && filters.platform.length > 0) {
          params.append('platform', filters.platform.join(','));
        }

        if (filters.post_type && filters.post_type.length > 0) {
          params.append('post_type', filters.post_type.join(','));
        }

        if (filters.source_post_id && filters.source_post_id.length > 0) {
          params.append('source_post_id', filters.source_post_id.join(','));
        }

        const url = `${API_BASE}/api/v1/analytics/breakdown?${params}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        setBrandData(data.items || []);
        
      } catch (err) {
        console.error('❌ Errore caricamento dati brand:', err);
        setBrandError(`Errore: ${err.message}`);
        setBrandData([]);
      } finally {
        setBrandLoading(false);
      }
    };

    // Debounce per evitare troppe chiamate
    const timeoutId = setTimeout(fetchBrandData, 300);
    return () => clearTimeout(timeoutId);
  }, [
    filters.project_id,
    filters.client_id,
    filters.posted_at_range,
    filters.platform,
    filters.post_type
  ]);

  // Funzione per formattare i numeri
  const formatNum = (num) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`.replace('.0', '');
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`.replace('.0', '');
    }
    return num.toLocaleString();
  };

  // Calcola totali per le percentuali
  const totalPosts = brandData.reduce((sum, b) => sum + (b.total_posts || 0), 0);
  const totalInteractions = brandData.reduce((sum, b) => sum + (b.total_interactions || 0), 0);
  const totalLikes = brandData.reduce((sum, b) => sum + (b.total_likes || 0), 0);
  const totalComments = brandData.reduce((sum, b) => sum + (b.total_comments || 0), 0);
  const totalShares = brandData.reduce((sum, b) => sum + (b.total_shares || 0), 0);
  const totalViews = brandData.reduce((sum, b) => sum + (b.total_views || 0), 0);

  if (brandLoading || loading) {
    return (
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontSize: '1rem' }}>
            <ThumbUp sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Brand Distribution
          </Typography>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={18} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (brandError) {
    return (
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontSize: '1rem' }}>
            <ThumbUp sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Brand Distribution
          </Typography>
          <Alert severity="error" sx={{ mt: 1 }}>
            <Typography variant="body2">
              Errore nel caricamento dati brand: {brandError}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!brandData.length) {
    return (
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontSize: '1rem' }}>
            <ThumbUp sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Brand Distribution
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" py={1.5}>
            Nessun dato brand disponibile
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const sortedBrands = [...brandData].sort((a, b) =>
    (b.total_interactions || 0) - (a.total_interactions || 0)
  );

  // Genera colori unici per i brand
  const generateBrandColor = (brandName) => {
    // Genera un colore basato sul nome del brand (hash function)
    let hash = 0;
    for (let i = 0; i < brandName.length; i++) {
      hash = brandName.charCodeAt(i) + ((hash << 5) - hash);
    }

    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#FFD166', '#06D6A0', '#118AB2',
      '#EF476F', '#073B4C', '#7209B7', '#F72585', '#3A86FF',
      '#FB5607', '#8338EC', '#FF006E', '#FFBE0B', '#3A86FF'
    ];

    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <Card sx={{ mb: 2.5 }}>
      <CardContent sx={{ p: 2 }}>
        {/* HEADER CON STATS */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
            <ThumbUp sx={{ mr: 0.75, fontSize: '1.1rem' }} /> Brand Distribution
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {sortedBrands.length} brands • {formatNum(totalPosts)} posts • {formatNum(totalInteractions)} interactions
          </Typography>
        </Box>

        {/* INTESTAZIONE TABELLA */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1.5fr repeat(6, 1fr)',
          gap: 1,
          mb: 1,
          px: 1,
          pb: 0.5,
          borderBottom: '2px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            BRAND
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            📝 POSTS
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            👍 INTERACTIONS
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            ❤️ LIKES
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            💬 COMMENTS
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            🔄 SHARES
          </Typography>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
            👁️ VIEWS
          </Typography>
        </Box>

        {/* CONTAINER CON SCROLL */}
        <Box sx={{
          maxHeight: 350,
          overflowY: 'auto',
          pr: 0.5,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '2px' },
          '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '2px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#555' }
        }}>
          {/* RIGHE DELLA TABELLA */}
          <Stack spacing={0.75}>
            {sortedBrands.map((brand, idx) => {
              const postsPct = totalPosts ? Math.round((brand.total_posts / totalPosts) * 100) : 0;
              const interactionsPct = totalInteractions ? Math.round((brand.total_interactions / totalInteractions) * 100) : 0;
              const likesPct = totalLikes ? Math.round((brand.total_likes / totalLikes) * 100) : 0;
              const commentsPct = totalComments ? Math.round((brand.total_comments / totalComments) * 100) : 0;
              const sharesPct = totalShares ? Math.round((brand.total_shares / totalShares) * 100) : 0;
              const viewsPct = totalViews ? Math.round((brand.total_views / totalViews) * 100) : 0;

              const brandColor = generateBrandColor(brand.dimension_value || 'unknown');

              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr repeat(6, 1fr)',
                    gap: 1,
                    alignItems: 'center',
                    p: 1,
                    borderRadius: 1,
                    bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  {/* COLONNA 1: NOME BRAND */}
                  <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: `${brandColor}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1,
                      flexShrink: 0
                    }}>
                      <Typography variant="caption" sx={{ color: brandColor, fontWeight: 'bold', fontSize: '0.7rem' }}>
                        {brand.dimension_value?.charAt(0).toUpperCase()}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        fontSize: '0.85rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={brand.dimension_value}
                    >
                      {brand.dimension_value}
                    </Typography>
                  </Box>

                  {/* COLONNA 2: POSTS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(brand.total_posts)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={postsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'primary.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'primary.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {postsPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 3: INTERACTIONS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(brand.total_interactions)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={interactionsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'success.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'success.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {interactionsPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 4: LIKES */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(brand.total_likes)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={likesPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'error.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'error.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {likesPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 5: COMMENTS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(brand.total_comments)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={commentsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'warning.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'warning.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {commentsPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 6: SHARES */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(brand.total_shares)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={sharesPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'secondary.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'secondary.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {sharesPct}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* COLONNA 7: VIEWS */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      {formatNum(brand.total_views)}
                    </Typography>
                    <Box sx={{ position: 'relative', height: 4 }}>
                      <LinearProgress
                        variant="determinate"
                        value={viewsPct}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'info.50',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 'info.main',
                            borderRadius: 2
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{
                        fontSize: '0.6rem',
                        position: 'absolute',
                        top: -15,
                        right: 2,
                        fontWeight: 'bold'
                      }}>
                        {viewsPct}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================
// 6. ERROR STATE COMPONENT
// ============================================

export const ErrorState = ({ error, onRetry }) => (
  <Card sx={{ mb: 3 }}>
    <CardContent sx={{ p: 4, textAlign: 'center' }}>
      <Alert
        severity="error"
        sx={{ mb: 3 }}
        action={
          onRetry && (
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          )
        }
      >
        <Typography variant="subtitle1" gutterBottom>
          Error Loading Data
        </Typography>
        <Typography variant="body2">
          {error || 'An unexpected error occurred'}
        </Typography>
      </Alert>
    </CardContent>
  </Card>
);

// ============================================
// 7. LOADING SKELETON
// ============================================

export const LoadingSkeleton = () => (
  <Box sx={{ width: '100%' }}>
    {/* Main KPIs Skeleton */}
    <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ width: 200, height: 32, bgcolor: 'grey.200', borderRadius: 1, mb: 3 }} />

        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Box sx={{
                height: 140,
                bgcolor: 'grey.100',
                borderRadius: 2,
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>

    {/* Platform Distribution Skeleton */}
    <Card sx={{ bgcolor: 'grey.50' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ width: 180, height: 28, bgcolor: 'grey.200', borderRadius: 1, mb: 3 }} />
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Box sx={{
                height: 180,
                bgcolor: 'grey.100',
                borderRadius: 2,
                animation: 'pulse 1.5s ease-in-out 0.2s infinite'
              }} />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  </Box>
);

// ============================================
// 9. DEFAULT EXPORT (Main Component)
// ============================================

const Scorecards = ({ dashboardData, loading = false, filters }) => {
  if (loading && !dashboardData) {
    return <LoadingSkeleton />;
  }

  if (!dashboardData && !loading) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No data available. Please select filters and load data.
      </Alert>
    );
  }

  return (
    <>
      {/* Main 6 KPI Cards */}
      <SocialPerformanceMetrics
        dashboardData={dashboardData}
        loading={loading}
      />

      {/* Platform Distribution */}
      <PlatformDistribution
        filters={filters}
        loading={loading}
      />
    </>
  );
};

// Export default
export default Scorecards;

// Export named per App.js (solo i componenti NON già esportati in cima)
export {
  SocialPerformanceMetrics,
  PlatformDistribution,
  PostTypeDistribution,
  BrandDistribution
  // ErrorState, LoadingSkeleton, KPICard sono già esportati in cima con 'export const'
};